/**
 * PropertyDetailModal — single modal for POS properties.
 *
 * One modal, two modes:
 *   - mode="view" → read-only display, no save/delete, opened by 👁️
 *   - mode="edit" → editable inputs, Save button, Settings tab available, opened by ✏️
 *
 * Identical layout, identical tabs, identical fields between modes —
 * only the interactivity changes. Mirrors the InvoiceDetailModal pattern
 * used elsewhere in Sentinel.
 *
 * Tabs are being migrated in chunks; see each TabStub for fallback guidance
 * until its owner tab is ready.
 */

import { useEffect, useState } from 'react';
import { formatDateMedium } from '../../../../utils/formatters';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { usePagePermission } from '../../../../hooks/usePagePermission';
import PropertyTabBody from './property-detail/PropertyTab';
import SetupTab from './property-detail/SetupTab';
import RecordsTab from './property-detail/RecordsTab';
import HomeTab from './property-detail/HomeTab';
import RoomsTab from './property-detail/RoomsTab';
import { usePropertyDetail } from './property-detail/usePropertyDetail';
import { STATUS_CONFIG, TIER_CONFIG, POS_GOLD, computeTemperature, TEMPERATURE_CONFIG } from './constants';
import type { PropertyDetailModalProps, PropertyTab } from './property-detail/types';

const TABS: Array<{ id: PropertyTab; label: string }> = [
  // Overview is the answer to "where is this property at?" — status,
  // charter, bedside cert, people, public pages. Replaces the prior
  // separate Home + Accreditation tabs which the team felt were two
  // halves of the same job. Default landing on existing properties.
  { id: 'overview', label: 'Overview' },
  // Property keeps its role: identity edit + listing edit. The
  // back-office surface for fixing data. Default landing in create
  // mode (Overview has nothing to show until the property exists).
  { id: 'property', label: 'Property' },
  // Records: Invoices, Service Certificates, Proposals, Agreements.
  { id: 'records', label: 'Records' },
  { id: 'rooms', label: 'Rooms' },
  // Setup is the reference workflow, not the hero. Last so it's there
  // when stuck but never in the way.
  { id: 'setup', label: 'Setup' },
];

interface PropertyDetailModalPropsExt extends PropertyDetailModalProps {
  setCertificatesContext?: (ctx: { type: string; id: string; name: string; propertyId: string; propertyName: string }) => void;
  setBookingsContext?: (ctx: { type: string; id: string; name: string; propertyId: string; propertyName: string }) => void;
  setServicesContext?: (ctx: { type: string; id: string; name: string; propertyId: string; propertyName: string }) => void;
}

export default function PropertyDetailModal({
  propertyId,
  mode,
  initialCustomerId,
  initialTab,
  onClose,
  onRefresh,
  setCertificatesContext,
  setBookingsContext,
  setServicesContext,
}: PropertyDetailModalPropsExt) {
  const {
    property,
    seededCustomer,
    form,
    setForm,
    loading,
    saving,
    deleting,
    error,
    activeTab,
    setActiveTab,
    handleSave,
    handleCreate,
    handleDelete,
  } = usePropertyDetail(supabase, propertyId, mode, onRefresh, initialCustomerId, initialTab);

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const { canAccess: canStartCertification } = usePagePermission('certification');

  // Most recent service evidence for this property — drives the "Start
  // Certification" button in view mode so it lands on a pre-seeded form.
  // Three fallback layers:
  //   1. A real service log on this property (modern flow)
  //   2. An invoice linked to the property via hosp_service_history
  //      (backfill path for POS properties whose history rows exist)
  //   3. The most recent invoice on the property's customer (final
  //      fallback for the long tail of POS properties imported with
  //      no hosp_service_history rows at all — e.g. Aviva
  //      Accommodation). Single-property customers are safe; on the
  //      rare multi-property customer the operator can still override
  //      on the cert form.
  const [certSeed, setCertSeed] = useState<string | null>(null);
  useEffect(() => {
    if (mode !== 'view' || !propertyId) {
      setCertSeed(null);
      return;
    }
    let cancelled = false;
    (async () => {
      // 1. Service log on this property
      const { data: log } = await supabase
        .from('service_logs')
        .select('id')
        .eq('property_id', propertyId)
        .neq('status', 'void')
        .order('service_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (log?.id) {
        setCertSeed(`service_log_id=${log.id as string}`);
        return;
      }
      // 2. Invoice linked via hosp_service_history trail
      const { data: hist } = await supabase
        .from('hosp_service_history')
        .select('invoice_id')
        .eq('property_id', propertyId)
        .not('invoice_id', 'is', null)
        .order('service_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const histInvoiceId = (hist as { invoice_id?: string } | null)?.invoice_id ?? null;
      if (histInvoiceId) {
        setCertSeed(`invoice_id=${histInvoiceId}`);
        return;
      }
      // 3. Most recent invoice on the property's customer
      const { data: prop } = await supabase
        .from('hosp_properties')
        .select('customer_id')
        .eq('id', propertyId)
        .maybeSingle();
      if (cancelled) return;
      const customerId = (prop as { customer_id?: string | null } | null)?.customer_id ?? null;
      if (!customerId) {
        setCertSeed(null);
        return;
      }
      const { data: inv } = await supabase
        .from('invoices')
        .select('id')
        .eq('customer_id', customerId)
        .order('invoice_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      const invId = (inv as { id?: string } | null)?.id ?? null;
      setCertSeed(invId ? `invoice_id=${invId}` : null);
    })();
    return () => { cancelled = true; };
  }, [mode, propertyId]);

  // In create mode the property doesn't exist yet — only the Overview tab
  // is meaningful (other tabs depend on a saved property).
  const visibleTabs = mode === 'create' ? TABS.filter(t => t.id === 'property') : TABS;

  async function onSaveClick() {
    setStatusMessage(null);
    await handleSave();
    setStatusMessage('Saved');
    setTimeout(() => setStatusMessage(null), 2500);
  }

  async function onCreateClick() {
    setStatusMessage(null);
    const created = await handleCreate();
    if (created?.id) {
      setStatusMessage('Property created');
      setTimeout(() => onClose(), 800);
    }
  }

  async function onSaveAndPublish() {
    setStatusMessage(null);
    const ok = await handleSave({ is_published: true });
    if (!ok) return;
    setStatusMessage('Saved & published, triggering rebuild…');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-website-rebuild`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({}),
        },
      );
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'Rebuild failed');
      setStatusMessage('Published. Live on peaceofsleep.vip in ~90 seconds.');
    } catch (err) {
      setStatusMessage(
        `Published, but rebuild trigger failed: ${err instanceof Error ? err.message : 'unknown error'}. Use Update Directory to retry.`,
      );
    }
    setTimeout(() => setStatusMessage(null), 5000);
  }

  // Temperature derived from assured_until via the shared helper.
  const temperature = computeTemperature(property?.assured_until);
  const statusCfg = property?.contract_status
    ? (STATUS_CONFIG as Record<string, { label: string; color: string; bg: string }>)[property.contract_status]
    : null;
  const tierCfg = property?.hospitality_tier
    ? (TIER_CONFIG as Record<string, { label: string; color: string; bg: string }>)[property.hospitality_tier]
    : null;
  const tempCfg = temperature ? TEMPERATURE_CONFIG[temperature] : null;

  const badgeStyle: React.CSSProperties = {
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    letterSpacing: '0.03em',
    padding: '0.1875rem 0.5rem',
    borderRadius: '999px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="modal-title">
              {mode === 'create'
                ? 'Add Property'
                : property?.property_name || 'Property'}
            </h2>
            <div style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-light, #6b7280)',
              marginTop: '0.25rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}>
              {mode === 'create' ? 'New property' : mode === 'view' ? 'Viewing' : 'Editing'}
              {mode !== 'create' && property?.charter_number ? ` · ${property.charter_number}` : ''}
            </div>
            {(statusCfg || tierCfg || tempCfg) && (
              <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                {statusCfg && (
                  <span style={{ ...badgeStyle, color: statusCfg.color, background: statusCfg.bg }}>
                    {statusCfg.label}
                  </span>
                )}
                {tierCfg && (
                  <span style={{ ...badgeStyle, color: tierCfg.color, background: tierCfg.bg }}>
                    {tierCfg.label}
                  </span>
                )}
                {tempCfg && (
                  <span style={{ ...badgeStyle, color: tempCfg.color, background: tempCfg.bg }}>
                    {tempCfg.label}
                  </span>
                )}
              </div>
            )}

            {/* Charter Suite actions moved to the Home tab on 2026-05-29
                so the modal header stays calm. */}
          </div>
          <div className="flex flex-col items-end gap-sm">
            <button className="modal-close" onClick={onClose} style={{ alignSelf: 'flex-end' }}>&times;</button>
            {property?.assured_until && (
              <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-light, #6b7280)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600,
                }}>
                  Assured until
                </div>
                <div style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 600,
                  color: tempCfg?.color || 'var(--color-text, #111827)',
                }}>
                  {formatDateMedium(property.assured_until)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* (Removed.) The pinned checklist strip duplicated the Setup tab
            content and crowded the header on every tab. Setup is now
            available as the last tab when an operator needs it. */}

        {/* Tabs */}
        <div
          role="tablist"
          style={{
            display: 'flex',
            gap: '0.25rem',
            borderBottom: '1px solid var(--color-border, #e5e7eb)',
            padding: '0 1.5rem',
            flexShrink: 0,
            overflowX: 'auto',
          }}
        >
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1rem',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${POS_GOLD}` : '2px solid transparent',
                color: activeTab === tab.id ? POS_GOLD : 'var(--color-text, #111827)',
                fontWeight: activeTab === tab.id ? 600 : 400,
                cursor: 'pointer',
                fontSize: 'var(--text-lg)',
                whiteSpace: 'nowrap',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1.5rem' }}>
          {loading || (mode !== 'create' && !property) ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {error && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: 'var(--color-error-bg, #fee2e2)',
                  color: 'var(--color-error, #dc2626)',
                  borderRadius: 'var(--radius-md)',
                  marginBottom: '1rem',
                  fontSize: 'var(--text-base)',
                }}>
                  {error}
                </div>
              )}
              {activeTab === 'setup' && property && (
                <SetupTab
                  supabase={supabase}
                  property={property}
                  onJumpToTab={setActiveTab}
                />
              )}
              {activeTab === 'property' && (
                <PropertyTabBody
                  supabase={supabase}
                  property={property}
                  seededCustomer={seededCustomer}
                  form={form}
                  setForm={(updater) => setForm(updater(form))}
                  mode={mode}
                  deleting={deleting}
                  onDelete={handleDelete}
                  onAfterDelete={onClose}
                  canDelete
                />
              )}
              {activeTab === 'overview' && property && (
                <HomeTab
                  supabase={supabase}
                  property={property}
                  form={form}
                  setForm={(updater) => setForm(updater(form))}
                  mode={mode}
                />
              )}
              {activeTab === 'rooms' && property && (
                <RoomsTab
                  supabase={supabase}
                  property={property}
                  mode={mode}
                  onRefresh={onRefresh}
                  setCertificatesContext={setCertificatesContext || (() => {})}
                  setBookingsContext={setBookingsContext || (() => {})}
                  setServicesContext={setServicesContext || (() => {})}
                />
              )}
              {activeTab === 'records' && property && (
                <RecordsTab
                  supabase={supabase}
                  property={property}
                  mode={mode}
                  onLinked={onRefresh}
                />
              )}
              {/* Documents tab dissolved on 2026-05-29. Proposals + Agreements
                  moved into Records. POS Certificate + Charter sections
                  were duplicates of Certification and were dropped.
                  Directory content lives inside the Property tab as the
                  Listing section. */}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ flexShrink: 0 }}>
          {statusMessage && (
            <div style={{ flex: 1, fontSize: 'var(--text-base)', color: 'var(--color-success, #16a34a)' }}>
              {statusMessage}
            </div>
          )}
          {mode === 'view' ? (
            <>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
              {canStartCertification && (
                <button
                  className="btn btn-outline"
                  onClick={() => {
                    if (!certSeed) return;
                    navigate(`/certification/submit?${certSeed}`);
                  }}
                  disabled={!certSeed}
                  title={
                    certSeed
                      ? 'Start the Peace of Sleep certificate seeded from the most recent service on this property'
                      : 'No service or invoice on this property yet — log a service first, then start the certificate from there.'
                  }
                >
                  Start Certification
                </button>
              )}
            </>
          ) : mode === 'create' ? (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={onCreateClick} disabled={saving}>
                {saving ? 'Creating…' : 'Create Property'}
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              {/* The footer Save saves the PROPERTY FORM only. The Rooms tab
                  has its own Save (it persists rooms, not the form), and the
                  footer Save reloads the property, which unmounts the Rooms
                  tab and discards unsaved room edits. So hide the footer Save
                  on Rooms — the tab's own Save is the correct one there. */}
              {activeTab !== 'rooms' && (
                <button className="btn btn-primary" onClick={onSaveClick} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              )}
              {activeTab === 'property' && (
                <button
                  className="btn btn-primary"
                  style={{ background: POS_GOLD, borderColor: POS_GOLD }}
                  onClick={onSaveAndPublish}
                  disabled={saving}
                  title="Saves and marks the listing as Published on peaceofsleep.vip"
                >
                  {saving ? 'Saving…' : 'Save & Publish'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
